<script lang="ts">
  import { addSheet, candidates, exampleLoaded, friends, mapView, placementMode, trips } from '../lib/state';
  import { loadExampleData, resetExampleData } from '../lib/example';
  import { geocode } from '../lib/api';
  import { authReady } from '../lib/auth';
  import { cloudLoading } from '../lib/sync';
  import AddressInput from './AddressInput.svelte';

  let dismissed = false;
  try { dismissed = localStorage.getItem('livenearby:welcome-dismissed') === 'true'; } catch {}
  function setDismissed(value: boolean) {
    dismissed = value;
    try { localStorage.setItem('livenearby:welcome-dismissed', String(value)); } catch {}
  }
  let search = '';
  let busy = false;
  let error = '';
  let chosenLocation = '';
  $: empty = !$friends.length && !$trips.length && !$candidates.length;

  async function findLocation() {
    if (!search.trim() || busy) return;
    busy = true;
    error = '';
    try {
      const place = await geocode(search.trim());
      mapView.set({ lat: place.lat, lng: place.lng, zoom: 13 });
      chosenLocation = place.formattedAddress;
    } catch {
      error = 'Could not find that location. Try a city, address, or neighborhood.';
    } finally {
      busy = false;
    }
  }

  function startOwn() {
    if (!confirm('Start fresh? This clears the example and any pins you added to it.')) return;
    resetExampleData();
    setDismissed(false);
    chosenLocation = '';
    search = '';
  }
</script>

{#if $exampleLoaded}
  <div class="example-banner" aria-label="Example data">
    <span><strong>Example data</strong><small>San Francisco · illustrative scores</small></span>
    <button on:click={startOwn}>Start your own</button>
  </div>
{:else if empty && $authReady && !$cloudLoading && $placementMode === 'none'}
  {#if dismissed}
    <button class="welcome-reopen" on:click={() => setDismissed(false)}>Choose a starting location</button>
  {:else}
  <section class="welcome-card" aria-labelledby="welcome-title">
    <button class="welcome-close" aria-label="Dismiss welcome" on:click={() => setDismissed(true)}>×</button>
    <p class="eyebrow">YOUR PEOPLE. YOUR PLACES.</p>
    <h2 id="welcome-title">{chosenLocation ? 'Make this map yours.' : 'Where would you love to live?'}</h2>
    {#if chosenLocation}
      <p>{chosenLocation}</p>
      <p>Start with one person or place you care about. Add homes when you’re ready to compare.</p>
      <button on:click={() => addSheet.set('friend')}>Add your first friend</button>
      <button class="text-button" on:click={() => addSheet.set('trip')}>Or add a favorite place</button>
      <button class="text-button" on:click={() => (chosenLocation = '')}>Change location</button>
    {:else}
      <p>Choose a city, address, or neighborhood, then add the people and places you care about.</p>
      <div class="search-row">
        <AddressInput bind:value={search} placeholder="City, address, or neighborhood" ariaLabel="City, address, or neighborhood" on:select={findLocation} on:submit={findLocation} />
        <button on:click={findLocation} disabled={busy || !search.trim()}>{busy ? 'Finding…' : 'Go'}</button>
      </div>
      {#if error}<p class="error" role="alert">{error}</p>{/if}
      <p class="hint">Or use the buttons below to add your first pin.</p>
    {/if}
    <button class="text-button example-link" on:click={loadExampleData}>Explore a San Francisco example →</button>
    <button class="text-button" on:click={() => setDismissed(true)}>Just explore the map</button>
  </section>
  {/if}
{/if}

<style>
  .welcome-close { position: absolute; top: 8px; right: 12px; background: transparent; color: var(--ink-soft); padding: 4px 8px; font-size: 24px; }
  .welcome-reopen { position: absolute; top: 80px; left: 16px; z-index: 30; background: var(--panel); color: var(--ink); border: 1px solid var(--line); border-radius: 12px; box-shadow: 0 4px 14px rgba(29,53,40,0.12); }

  .welcome-card {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 30;
    width: min(420px, calc(100% - 32px));
    padding: 28px;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: var(--panel);
    box-shadow: 0 12px 40px rgba(29, 53, 40, 0.12);
  }
  h2 { margin: 0 0 12px; font: italic 34px/1.05 var(--serif); }
  p { color: var(--ink-soft); font-size: 13px; line-height: 1.5; }
  .eyebrow { margin: 0 0 14px; font-size: 10px; font-weight: 600; letter-spacing: 0.1em; }
  .search-row { display: flex; gap: 8px; margin-top: 18px; }
  .search-row :global(.ac-wrap) { flex: 1; min-width: 0; }
  .search-row button { flex: none; }
  .hint { font-size: 11px; }
  .error { color: var(--warn); }
  .text-button { display: block; padding: 8px 0; background: transparent; color: var(--accent); font-size: 12px; text-align: left; }
  .text-button:hover { background: transparent; text-decoration: underline; }
  .example-link { margin-top: 12px; border-top: 1px solid var(--line); width: 100%; padding-top: 16px; }
  .example-banner {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 35;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 12px;
    box-shadow: 0 4px 14px rgba(29, 53, 40, 0.12);
  }
  .example-banner strong { font-size: 12px; }
  .example-banner small { display: block; color: var(--ink-soft); font-size: 10px; }
  .example-banner button { padding: 8px 10px; font-size: 11px; }
  @media (min-width: 769px) and (max-width: 1100px) {
    .example-banner { top: auto; bottom: 116px; }
  }
  @media (max-width: 768px) {
    .welcome-card { top: max(84px, calc(env(safe-area-inset-top, 0px) + 84px)); transform: translateX(-50%); padding: 22px; }
    h2 { font-size: 30px; }
    .example-banner { top: max(76px, calc(env(safe-area-inset-top, 0px) + 76px)); left: 12px; right: 12px; justify-content: space-between; }
  }
</style>
